import { useEffect, useMemo, useState } from "react";
import jexl from 'jexl'
import DOMPurify from 'dompurify';
import { GoabFormItem, GoabInputNumber, GoabInputText,
  GoabTextArea, GoabDatePicker, GoabCheckbox, GoabDropdown, GoabFileUploadInput,
  GoabDropdownItem, GoabRadioGroup, GoabRadioItem, GoabInput } from "@abgov/react-components";
import DisplayContent from "../Forms/DisplayContent";
import { toTitleCase, formatPhoneNumber,
  getProvinceName,
  getFullCountryName,
  getFullName,
  getGenderFromKey,
  getDate,
  getAddressDisplay,
  getPhoneDisplay,
  getPaymentMethod,
  getETransferMethod,
  domainKey,
  validatePhoneInputField
 } from "../../../utils/globalUtility";
import { getBankNameFromNumber } from "../../../utils/bankValidation";
import { handleGA } from "../../../utils/analytics";

 const valueSeparator = '|';

const setFormValue = (form, name, value, setFormData) => {
  
  const nameArr = name?.split(valueSeparator);
  const valueArr = typeof value === 'string' ? value?.split(valueSeparator) : [value];
  if (nameArr?.length > 1 && valueArr?.length > 1) {
    name = nameArr[0];
    value = valueArr[0];
    form.setFieldValue(nameArr[1], valueArr[1]);
  }
  form.setFieldValue(name, value);
  if (setFormData) {
    let formValue = {...form.values, [name]: value};
    if (nameArr?.length > 1 && valueArr?.length > 1) {
      formValue = {...form.values, [nameArr[1]]:  valueArr[1]};
    }
    setFormData(formValue);
  }
}

const handleChange = (form, name, value, setFormData, titleCase = false, phoneType = false, extraName = undefined, extraValue = undefined) => {

  let fieldValue = value;
  if (titleCase) {
    fieldValue = toTitleCase(value);
  } 
  else if (phoneType) {
    const formattedValue = formatPhoneNumber(value);
    if (!value.includes(formattedValue)) {
      fieldValue = formattedValue;
    }
  }
  delete form.errors[name];
  if (extraName && extraValue) {
    name = name + valueSeparator + extraName;
    fieldValue = fieldValue + valueSeparator + extraValue;
  }
  setFormValue(form, name, fieldValue, setFormData);
};

const handleBlur = (form, name) => {
  form.setFieldTouched(name, true);
};

// The form only validates on blur, so opt-in fields need a nudge to keep the Next button in sync.
// form.values is still a render behind here, so merge the new value in before validating.
const revalidateOnChange = (form, field, changes) => {
  if (field?.validations?.validateOnChange) {
    form.validateForm({ ...form.values, ...changes });
  }
};


const errorValue = (form, name, getResourceValue, context, disabled) => {
  if(!!disabled && !!context){
    if(jexl.evalSync(disabled, {...context, form: form.values})){
      delete form.errors[name];
    }
  }
  return form?.errors?.[name] &&
    form?.touched?.[name] &&
    getResourceValue({ key: 'RESOURCES@' + form?.errors?.[name] });
}

const requirementValue = (field) => {
  const isRequired = field.validations?.required === true || (field.validations?.required?.text ?? false);
  return isRequired ? '' : 'optional';
}

const maskValue = (val, suffix) => {
  if (!val) return '';
  const strVal = val.toString();
  if (strVal?.length <= suffix) return strVal;
  return '*'.repeat(strVal?.length - suffix) + strVal.slice(-suffix);
};

export const DynamicFormValue = ({ field, form, getResourceValue, getRefData, context, setFormData, clickHandlers }) => {

  let value = '';

  const updatedContext = useMemo(() => 
    (!!field.properties.stepName && context.getStepData) 
      ? { ...context, [field.properties.stepName]: context.getStepData(field.properties.stepName) }
      : context,
    [field.properties.stepName, context]
  );

  useEffect(() => {
    if (field.properties.method) {
      try {
        if (field.properties.method === 'getRefValue' && field.properties.updateFieldsWithParams) {
          const key = jexl.evalSync(field.properties.params.key, {...updatedContext, form: form?.values || {}});
          
          if (form.values[field.properties.name] !== key) {
            setFormValue(form, field.properties.name, key, setFormData); 
          }
        } else if (field.properties.updateFieldsWithParams) {
          const params = Object.keys(field.properties.params ?? []).map(key => {
            return field.properties.params[key].startsWith('RESOURCES@') ? getResourceValue({key: field.properties.params[key]}) :
              jexl.evalSync(field.properties.params[key], {...updatedContext, form: form?.values || {}});
          });
          
          Object.keys(field.properties.params ?? []).forEach((name, index) => {
            if (form.values[name] !== params[index]) {
              setFormValue(form, name, params[index], setFormData);
            }       
          });
        }
      } catch(ex) {

      }
    }

    // Handle content-based updates
    if (field.properties.content && field.properties.updateFieldsWithParams) {
      Object.keys(field.properties?.params || []).forEach((paramKey) => {
          let paramValue = field.properties.params[paramKey];
          if (paramValue.includes('|mask')) {
            const paramValueArr = paramValue.split('|');
            paramValue = paramValueArr[0];
          }
          
          try {
              const exprValue = jexl.evalSync(paramValue, {...updatedContext, form: form});
              if (form.values[paramKey] !== exprValue) {
                setFormValue(form, paramKey, exprValue, setFormData);
              }
          } catch(ex) {

          }
      });
    }
  }, [
    field.properties.method, 
    field.properties.updateFieldsWithParams,
    field.properties.name,
    field.properties.content,
    field.properties.params
  ]);

  if (field.properties.method) {
    const methods = {
      getFullName,
      getProvinceName,
      getFullCountryName,
      getGenderFromKey,
      getDate,
      getAddressDisplay,
      getPhoneDisplay,
      getBankNameFromNumber,
      getPaymentMethod,
      getETransferMethod
    };
    try {
      if (field.properties.method === 'getRefValue') {
        const refData = getRefData({category: field.properties.params.category, domainKey});
        const key = jexl.evalSync(field.properties.params.key, {...updatedContext, form: form?.values || {}});
        value = refData ? refData?.find(ref => ref.code === key)?.description : key;
      } else {
        const method = methods[field.properties.method];
        if (method) {
          const evaluatedParams = Object.keys(field.properties.params ?? []).map(key => {
            return field.properties.params[key].startsWith('RESOURCES@') ? getResourceValue({key: field.properties.params[key]}) :
              jexl.evalSync(field.properties.params[key], {...updatedContext, form: form?.values || {}});
          });
          
          let content = method(...evaluatedParams);
          if (!content && !!field.properties.defaultContent) {
            content = getResourceValue({key:'RESOURCES@' + field.properties.defaultContent});
          }
          value = <DisplayContent content={content} clickHandlers={clickHandlers} />;
        }
      }
    }
    catch(ex) {

    }
  }
  
  if (field.properties.content) {
    let content = getResourceValue({key:'RESOURCES@' + field.properties.content});

    // Replace placeholders in the value with provided params
    Object.keys(field.properties?.params || []).forEach((paramKey) => {
        let paramValue = field.properties.params[paramKey];
        let mask = false;
        let unMaskSuffix = 0;
        if (paramValue.includes('|mask')) {
          const paramValueArr = paramValue.split('|');
          paramValue = paramValueArr[0];
          mask = true;
          unMaskSuffix = paramValueArr[2] ?? 4;
        }
        const regex = new RegExp(`{${paramKey}}`, 'g');
        let value = getResourceValue({key:'RESOURCES@' + paramValue});
        try {
            const exprValue = jexl.evalSync(paramValue, {...updatedContext, form: form});
            if (exprValue || paramValue.startsWith('row') || (!!field.properties.stepName && paramValue.startsWith(field.properties.stepName))) {
                value = exprValue ?? '';
            }
        }
        catch(ex) {
          // Handle error
        }
        content = content.replace(regex, mask ? maskValue(value, +unMaskSuffix) : value);
    });

  const isEmptyContent =
    !content ||
    DOMPurify.sanitize(content, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim() === '';

    if (isEmptyContent && field.properties.defaultContent) {
      content = getResourceValue({
        key: 'RESOURCES@' + field.properties.defaultContent,
      });
    }
    value = <DisplayContent content={content} clickHandlers={clickHandlers} />;
  }
  
  const { name, label } = field.properties;

  return (
    <div>
      <label style={{fontWeight: 'bold'}} htmlFor={name}>{getResourceValue({key:'RESOURCES@' + label})}</label>
      <div style={{ overflowWrap: "anywhere" }}>{value}</div>
    </div>
  );
};

export const DynamicFormTextInput = ({ field, form, getResourceValue, context, setFormData }) => {

  const { name, label, titleCase, helpText, type, disabled, value, mask, unMaskedSuffix = 4, stepName, copyPasteDisabled, maxLength, ...otherProps } = field.properties;
  const error = errorValue(form, name, getResourceValue, context, disabled);

  const [max, setMax] = useState(type === 'tel' ? (value?.startsWith?.("+") ? 18 : 10) : maxLength);

  const updatedContext = useMemo(() => 
    (!!stepName && context.getStepData) 
      ? { ...context, [stepName]: context.getStepData(stepName) }
      : context,
    [stepName, context]
  );
  
  let exprDisabledValue = undefined;
  if (disabled) {
    exprDisabledValue = jexl.evalSync(disabled, {...updatedContext, form: form.values});
  }

  let exprValue = undefined;
  if (value) {
    exprValue = jexl.evalSync(value, {...updatedContext, form: form.values});
  }

  const [isFocused, setIsFocused] = useState(false);
  const inputValue = exprDisabledValue ? exprValue : form.values?.[name] || exprValue;

  const stepDataValue = useMemo(() => updatedContext?.stepData?.[name], [updatedContext, name]);

  useEffect(() => {
    if (form.values[name] !== inputValue && (!!form.values[name] || ((!!stepDataValue && !form.dirty) || !!inputValue))) {
      setFormValue(form, name, exprDisabledValue ? inputValue : updatedContext?.stepData?.[name] || inputValue, setFormData);
    }
  }, [inputValue, exprDisabledValue, stepDataValue, name]);

  const handleKeyDownPhone = (event) => {
    if (event.repeat && event.key === "+") {
      event.preventDefault();
      return;
    }
  }

  useEffect(() => {
    if(type === 'tel'){
      setMax(inputValue?.startsWith("+") ? 18 : 10);
    }
  }, [inputValue, type])

  useEffect(() => {
    if(type === 'tel'){
        //checking for the type phone
        const phoneInput = document.getElementById(`dynamicTextInput-${name}`)
        const handleBeforeInput = (e) => {
          validatePhoneInputField(e);
        };
        phoneInput.addEventListener('keydown', handleKeyDownPhone);
        phoneInput.addEventListener('beforeinput', handleBeforeInput);

        // Cleanup function to remove event listeners
        return () => {
          phoneInput.removeEventListener('keydown', handleKeyDownPhone);
          phoneInput.removeEventListener('beforeinput', handleBeforeInput);
        }
    }
  },[type, name])

  useEffect(() => {
    //checking for the property copyPasteDisabled is true
    if(copyPasteDisabled){          
      const currentInputField = document.getElementById(`dynamicTextInput-${name}`)
      const handlePaste = (e) => {
        e.preventDefault();
      };            
      currentInputField.addEventListener('paste', handlePaste);     
      return () => {
        currentInputField.removeEventListener('paste', handlePaste);
      }
    }
  },[copyPasteDisabled, name])

  if (!field?.properties){
    return <></>
  }

  return (
    <GoabFormItem
      label={getResourceValue({ key: 'RESOURCES@' + label })}
      requirement={requirementValue(field)}
      error={error}
      helpText={helpText}
      id={name}
    >
      <GoabInputText
        {...otherProps}
        id={`dynamicTextInput-${name}`}
        name={name}
        value={isFocused || !mask ? inputValue : maskValue(inputValue, unMaskedSuffix ?? 4)}
        disabled={exprDisabledValue}
        error={error}
        onFocus={() => setIsFocused(true)}
        onBlur={({name}) => {setIsFocused(false); handleBlur(form, name)}}
        onChange={({name, value}) => {handleChange(form, name, value, setFormData, titleCase, type === 'tel')}}
        maxLength={max}
      />
    </GoabFormItem>
  );
};

export const DynamicFormNumberInput = ({ field, form, getResourceValue, context, setFormData }) => {
  
  const { name, label, helpText, disabled, value, mask, unMaskedSuffix=4, ...otherProps } = field.properties;
  const error = errorValue(form, name, getResourceValue);

  let exprDisabledValue = undefined;
  if (disabled) {
    exprDisabledValue = jexl.evalSync(disabled, {...context, form: form.values});
  }

  let exprValue = undefined;
  useEffect(() => {
    if (value) {
      exprValue = jexl.evalSync(value, {...context, form: form.values});
      if (!form.values[name] && (!!context?.stepData?.[name] || !!exprValue)) {
        setFormValue(form, name, context?.stepData?.[name] || exprValue, setFormData);
      }
    }
  }, [value, name]);

  const [isFocused, setIsFocused] = useState(false);
  const inputValue = exprDisabledValue ? exprValue : form.values?.[name] || exprValue;

  if (!field?.properties){
    return <></>
  }

  return (
      <GoabFormItem label={getResourceValue({key:'RESOURCES@' + label})}
          requirement={requirementValue(field)}
          error={error}
          helpText={helpText}
          id={name}>
        <GoabInputNumber {...otherProps}
          name={name}
          value={isFocused || !mask ? inputValue : maskValue(inputValue, unMaskedSuffix ?? 4)}
          disabled={exprDisabledValue}
          error={error}
          onFocus={() => setIsFocused(true)}
          onBlur={({name}) => {setIsFocused(false); handleBlur(form, name)}}
          onChange={({name, value}) => handleChange(form, name, value, setFormData)}
        />
      </GoabFormItem>
  );
};

export const DynamicFormTextAreaInput = ({ field, form, getResourceValue, context, setFormData }) => {

  const { name, label, helpText, disabled, value, ...otherProps } = field.properties;
  const error = errorValue(form, name, getResourceValue);

  let exprDisabledValue = undefined;
  if (disabled) {
    exprDisabledValue = jexl.evalSync(disabled, {...context, form: form.values});
  }

  let exprValue = undefined;
  useEffect(() => {
    if (value) {
      exprValue = jexl.evalSync(value, {...context, form: form.values});
      if (!form.values[name] && (!!context?.stepData?.[name] || !!exprValue)) {
        setFormValue(form, name, context?.stepData?.[name] || exprValue, setFormData);
      }
    }
  }, [value, name]);
  
  const inputValue = exprDisabledValue ? exprValue : form.values?.[name] || exprValue;

  if (!field?.properties){
    return <></>
  }

  return (
      <GoabFormItem label={getResourceValue({key:'RESOURCES@' + label})}
          requirement={requirementValue(field)}
          error={error}
          helpText={helpText}
          id={name}>
        <GoabTextArea {...otherProps}
          name={name}
          value={inputValue}
          disabled={exprDisabledValue}
          error={error}
          onChange={({name, value}) => handleChange(form, name, value, setFormData)}
        />
      </GoabFormItem>
  );
};

export const DynamicFormDateInput = ({ field, form, getResourceValue, context, setFormData }) => {

  const { name, label, helpText, disabled, value, ...otherProps } = field.properties;
  const error = errorValue(form, name, getResourceValue);

  let exprDisabledValue = undefined;
  if (disabled) {
    exprDisabledValue = jexl.evalSync(disabled, {...context, form: form.values});
  }

  let exprValue = undefined;
  useEffect(() => {
    if (value) {
      exprValue = jexl.evalSync(value, {...context, form: form.values});
      if (!form.values[name] && (!!context?.stepData?.[name] || !!exprValue)) {
        setFormValue(form, name, context?.stepData?.[name] || exprValue, setFormData);
      }
    }
  }, [value, name]);

  const inputValue = exprDisabledValue ? exprValue : form.values?.[name] || exprValue;

  if (!field?.properties){
    return <></>
  }

  return (
      <GoabFormItem label={getResourceValue({key:'RESOURCES@' + label})}
          requirement={requirementValue(field)}
          error={error}
          helpText={helpText}
          id={name}>
        <GoabDatePicker {...otherProps}
          name={name}
          value={inputValue}
          disabled={exprDisabledValue}
          error={error}
          onBlur={({name}) => {handleBlur(form, name)}}
          onChange={({name, value}) => handleChange(form, name, value, setFormData)}
        />
      </GoabFormItem>
  );
};

export const DynamicFormCheckInput = ({ field, form, getResourceValue, context, setFormData }) => {

  const { name, label, text, disabled, ...otherProps } = field.properties;
  const error = errorValue(form, name, getResourceValue);

  const updatedContext = useMemo(() => 
    (!!field.properties.stepName && context.getStepData) 
      ? { ...context, [field.properties.stepName]: context.getStepData(field.properties.stepName) }
      : context,
    [field.properties.stepName, context]
  );
  let exprDisabledValue = undefined;
  if (disabled) {
    exprDisabledValue = jexl.evalSync(disabled, {...updatedContext, form: form.values});
  }

  if (!field?.properties){
    return <></>
  }

  return (
      <GoabFormItem label={getResourceValue({key:'RESOURCES@' + label})}
          requirement={requirementValue(field)}
          error={error}
          id={name}>
        <GoabCheckbox {...otherProps}
          name={name}
          text={getResourceValue({key:'RESOURCES@' + text})}
          error={error}
          disabled={exprDisabledValue}
          checked={form?.values?.[`${name}`]}
          onBlur={({name}) => {handleBlur(form, name)}}
          onChange={({name, checked, value}) => {
            handleGA(field, checked);
            handleChange(form, name, checked, setFormData);
          }}
        />
      </GoabFormItem>
  );
};

export const DynamicFormDropdownInput = ({ field, form, getResourceValue, getRefData, context, setFormData }) => {

  const { label, name, placeholder, options, helpText, disabled, category, value, ...otherProps } = field.properties;
  const error = errorValue(form, name, getResourceValue);

  let exprValue = undefined;
  useEffect(() => {
    if (value) {
      exprValue = jexl.evalSync(value, { ...context, form: form.values });
      if (!form.values[name] && (!!context?.stepData?.[name] || !!exprValue)) {
          setFormValue(form, name, context?.stepData?.[name] || exprValue, setFormData);
      }
    }
  }, [value, name]);

  let exprDisabledValue = undefined;
  if (disabled) {
    exprDisabledValue = jexl.evalSync(disabled, {...context, form: form.values});
  }

  const items =
    category && getRefData
      ? (getRefData({ category, domainKey }) ?? []).map(({ description, code }) => ({
          label: description,
          value: code
        }))
      : (options ?? []).map(({ label, value }) => ({
          label: getResourceValue({ key: 'RESOURCES@' + label }),
          value
        }));

  if (!field?.properties){
    return <></>
  }

  return (
      <GoabFormItem label={getResourceValue({key:'RESOURCES@' + label})}
          requirement={requirementValue(field)}
          error={error}
          id={name}>
        <GoabDropdown {...otherProps}
          name={name}
          value={form?.values[name] || exprValue}
          placeholder={getResourceValue({key: "RESOURCES@" + (placeholder ?? 'global.select.placeholder')})}
          arialabelledby={name}
          disabled={exprDisabledValue}
          error={error}
          onBlur={({name}) => {handleBlur(form, name)}}
          onChange={({name, value}) => {
            handleChange(form, name, value, setFormData);
          }}
        >
          {items.map(({ label, value }) => (
            <GoabDropdownItem key={value} value={value} label={getResourceValue({key:'RESOURCES@' + label})} />
          ))}
        </GoabDropdown>
      </GoabFormItem>
  );
};

export const DynamicFormRadioInput = ({ field, form, getResourceValue, getRefData, context, setFormData }) => {

  const { label, helpText, helpTextCondition, name, options, category, context: fieldContext, value, extraValue, extraName, ...otherProps } = field.properties;
  const error = errorValue(form, name, getResourceValue);

  let exprValue = undefined;
  useEffect(() => {
    if (value) {
      exprValue = jexl.evalSync(value, {...context, form: form.values});
      if (!form.values[name] && (!!context?.stepData?.[name] || !!exprValue)) {
        setFormValue(form, name, context?.stepData?.[name] || exprValue, setFormData);
      }
      else if (!form.values[name] && value) {
        setFormValue(form, name, value, setFormData);
      }
    }
  }, [value, name]);

  const items =
    fieldContext === 'availableEvents' && context?.availableEvents && context?.programEvents
      ? (context.user.isProxyUser ? context.programEvents : context.availableEvents ?? []).map(evt => ({
          label: evt.name,
          value: evt.code,
          extraValue: extraValue && extraName ? evt[extraValue] : ''
        }))
      : category && getRefData
      ? (getRefData({ category, domainKey }) ?? []).map(evt => ({
          label: evt.description,
          value: evt.code,
          extraValue: extraValue && extraName ? evt[extraValue] : ''
        }))
      : (options ?? []).map(({ label, value, description }) => ({
          label: getResourceValue({ key: 'RESOURCES@' + label }),
          value,
          description: !!description ? getResourceValue({ key: 'RESOURCES@' + description }) : undefined
        }));

  if (!field?.properties){
    return <></>
  }

  return (
      <GoabFormItem label={getResourceValue({key:'RESOURCES@' + label})}
          helpText={!helpTextCondition || jexl.evalSync(helpTextCondition, {...form.values}) ? getResourceValue({key:'RESOURCES@' + helpText}) : ''}
          requirement={requirementValue(field)}
          error={error}
          id={name}>
        <GoabRadioGroup {...otherProps}
          name={name}
          value={form?.values[name] || exprValue}
          // value='e-transfer'
          arialabelledby={name}
          error={error}
          onBlur={({name}) => {handleBlur(form, name)}}
          onChange={({name, value}) => {
            handleGA(field, value);
            const changes = { [name]: value };
            if (extraValue && extraName) {
              const evalExtraValue = items.find(item => item.value === value)?.extraValue;
              changes[extraName] = evalExtraValue;
              handleChange(form, name, value, setFormData, false, false, extraName, evalExtraValue);
            } else {
              handleChange(form, name, value, setFormData);
            }
            revalidateOnChange(form, field, changes);
          }}
        >
          {items.map(({ label, value, description }) => (
            <GoabRadioItem key={value} value={value} label={getResourceValue({key:'RESOURCES@' + label})} description={description} {...field.properties?.items} />
          ))}
        </GoabRadioGroup>
      </GoabFormItem>
  );
};

export const DynamicFormFileInput = ({ field, form, getResourceValue }) => {
  if (!field?.properties){
    return <></>
  }
  const { label, name } = field.properties;
  const error = errorValue(form, name, getResourceValue);

  return (
    <GoabFormItem label={getResourceValue({key:'RESOURCES@' + label})}
          requirement={requirementValue(field)}
          error={error}
          id={name}>
      <GoabFileUploadInput {...field.properties} />
    </GoabFormItem>
  );
};

export const DynamicFormInput = ({ field, form, getResourceValue, context, setFormData }) => {
  
  useEffect(() => {
    if (field?.properties?.type === 'hidden') {
      const { name, value } = field.properties;
      let exprValue = value ?? '';
      if (value === '{DATE}') {
        exprValue = new Date().toISOString();
      } else if (value === '{VERSION}') {
        exprValue = context?.templateVersion ?? '';
      }

      if (!!exprValue && form.values[name] !== exprValue) {
        setFormValue(form, name, exprValue, setFormData);
      }
    }
  }, [field?.properties?.type, field?.properties?.value, field?.properties?.name, context?.templateVersion]);
  
  if (!field?.properties){
    return <></>
  }

  const { label, name, value } = field.properties;
  
  if (field.properties.type === 'hidden') {
    let exprValue = value ?? '';
    if (value === '{DATE}') {
      exprValue = new Date().toISOString();
    } else if (value === '{VERSION}') {
      exprValue = context?.templateVersion ?? '';
    }
    
    return <input type="hidden" id={name} name={name} value={exprValue}></input>
  }

  const error = errorValue(form, name, getResourceValue);
  
  return (
    <GoabFormItem label={getResourceValue({key:'RESOURCES@' + label})}
          requirement={requirementValue(field)}
          error={error}
          id={name}>
      <GoabInput {...field.properties}
          value={form.values?.[name]}
          onBlur={({name}) => {handleBlur(form, name)}}
          onChange={({name, value}) => handleChange(form, name, value, setFormData)}
          error={error}
        />
    </GoabFormItem>
  );
};